import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { brand, homeStats } from "@/mocks/site";
import { cases } from "@/mocks/cases";
import StatusBadge from "@/components/base/StatusBadge";

const featured = cases[0];

export default function HeroSection() {
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = keyword.trim();
    navigate(query ? `/cases?q=${encodeURIComponent(query)}` : "/cases");
  };

  return (
    <section className="relative w-full overflow-hidden border-b border-background-200 bg-background-100">
      <div className="paper-grid absolute inset-0 opacity-50" aria-hidden="true" />
      <div className="relative mx-auto max-w-[1440px] px-4 py-10 md:px-6 md:py-16">
        <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-7">
            <p className="flex items-center gap-2 text-[11px] font-medium tracking-[0.24em] text-secondary-600">
              <span className="h-px w-8 bg-secondary-400" aria-hidden="true" />
              第 04 期 · 舆论反转观察
            </p>
            <h1 className="mt-5 font-heading text-3xl font-bold leading-[1.25] tracking-tight text-foreground-950 md:text-5xl">
              反转不是终点，
              <br />
              看清才是
            </h1>
            <p className="mt-5 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {brand.intro}
            </p>

            <form onSubmit={handleSearch} role="search" className="mt-7 flex w-full max-w-lg items-center gap-2">
              <label htmlFor="hero-search" className="sr-only">
                搜索案例关键词
              </label>
              <div className="relative flex-1">
                <i
                  className="ri-search-2-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"
                  aria-hidden="true"
                />
                <input
                  id="hero-search"
                  name="keyword"
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索事件、分类或关键词，例如「时间线」"
                  className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <button
                type="submit"
                className="flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md bg-primary-800 px-4 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                检索
              </button>
            </form>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to="/cases"
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-foreground-800 px-4 py-2.5 text-sm text-foreground-900 transition-colors hover:bg-foreground-900 hover:text-background-50"
              >
                <i className="ri-archive-2-line text-base" aria-hidden="true" />
                浏览全部档案
              </Link>
              <Link
                to="/guide"
                className="flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm text-secondary-700 transition-colors hover:text-secondary-900"
              >
                <i className="ri-arrow-right-circle-line text-base" aria-hidden="true" />
                先学四步核验法
              </Link>
            </div>

            <dl className="mt-10 grid grid-cols-2 gap-x-6 gap-y-6 border-t border-background-300 pt-7 sm:grid-cols-4">
              {homeStats.map((stat, index) => (
                <div key={stat.label}>
                  <dd
                    className={`font-heading text-2xl font-bold md:text-3xl ${
                      index === 2 ? "text-accent-600" : "text-foreground-950"
                    }`}
                  >
                    {stat.value}
                  </dd>
                  <dt className="mt-1 text-[11px] tracking-wide text-foreground-500">{stat.label}</dt>
                </div>
              ))}
            </dl>
          </div>

          <div className="lg:col-span-5">
            <article className="group overflow-hidden rounded-lg border border-background-300 bg-background-50">
              <div className="relative h-56 w-full overflow-hidden bg-background-200 md:h-64">
                <img
                  src={featured.cover}
                  alt={`${featured.title}案例配图`}
                  title={`${featured.title} - 本周重点反转`}
                  className="h-56 w-full object-top transition-transform duration-500 group-hover:scale-[1.03] md:h-64"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/10 to-transparent" aria-hidden="true" />
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="flex items-center gap-2 text-[11px] font-medium tracking-widest text-accent-300">
                    <i className="ri-flashlight-line text-sm" aria-hidden="true" />
                    本周重点反转
                  </p>
                  <h2 className="mt-2 font-heading text-lg font-semibold leading-snug text-background-50">
                    {featured.title}
                  </h2>
                </div>
              </div>
              <div className="p-4 md:p-5">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge label={featured.status} />
                  <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-medium text-secondary-900">
                    <i className="ri-price-tag-3-line text-xs" aria-hidden="true" />
                    {featured.category}
                  </span>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{featured.summary}</p>
                <Link
                  to={`/cases/${featured.slug}`}
                  className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-700 transition-colors hover:text-primary-800"
                >
                  查看完整时间线
                  <i className="ri-arrow-right-line text-base" aria-hidden="true" />
                </Link>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}